import * as React from 'react';

import { cn } from './cn';

// Public Typography contract:
//   - Text:    body | body-sm | caption | overline | control
//   - Heading: title-sm | title | title-lg | display
//   - tone:    foreground | muted | background | info | success | warning
//              | destructive | inherit
//   - as:      Text → span (default) | p | em | strong | label
//              Heading → variant-resolved (display→h1, title-lg→h2,
//              title→h3, title-sm→h4) | h1-h6 | span | label
//
// Each variant is a complete recipe (size + weight + transform + leading).
// No size/weight/transform props — pick a variant. Heading's default `as`
// follows from the variant so the heading level matches the visual scale
// without consumers having to spell it out.
//
// The `as` prop exists for HTML semantics (paragraph vs span, heading
// hierarchy, form-label association) and is restricted to text-bearing
// elements at runtime. Passing `as="div"`, `as="section"`, etc. throws —
// typography is for text. Default is `<span>` for Text (inline) so most
// callers can omit the prop; pass `as="p"` only when paragraph semantics
// matter (helps screen readers and content outline).
//
// The class maps are private to this file. Other ui/ primitives compose
// <Text>/<Heading> instead of importing class strings. Edge cases
// (Schedule grid, Pagination flex, fields/inputs that can't have
// children) bake their own recipe locally with a comment pointing back
// here as the source of truth.

const TEXT_ALLOWED_ELEMENTS = new Set(['span', 'p', 'em', 'strong', 'label']);
const HEADING_ALLOWED_ELEMENTS = new Set(['h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'span', 'label']);

// Each Heading variant maps to a semantic heading level by default. Consumers
// who need a different level (or a non-heading element like span/label) pass
// `as=` explicitly. The mapping mirrors the visual hierarchy:
//   display   → page-level hero        → <h1>
//   title-lg  → major section          → <h2>
//   title     → minor section          → <h3>
//   title-sm  → card / widget heading  → <h4>
const HEADING_VARIANT_DEFAULT_ELEMENTS = {
    display: 'h1',
    'title-lg': 'h2',
    title: 'h3',
    'title-sm': 'h4'
};

const assertAllowedElement = (element, allowed, primitiveName) => {
    if (!allowed.has(element)) {
        throw new Error(
            `<${primitiveName} as="${element}"> is not allowed. Typography is for text — use one of: ${[...allowed].join(', ')}.`
        );
    }
};

const WM_TEXT_VARIANT_CLASSNAMES = {
    body: 'tw-text-sm tw-leading-6',
    'body-sm': 'tw-text-xs tw-leading-5',
    caption: 'tw-text-caption tw-leading-5',
    overline: 'tw-text-caption tw-font-semibold tw-uppercase tw-tracking-wide tw-leading-5',
    control: 'tw-text-sm tw-font-medium tw-leading-5'
};

const WM_HEADING_VARIANT_CLASSNAMES = {
    'title-sm': 'tw-text-base tw-font-semibold tw-leading-tight',
    title: 'tw-text-lg tw-font-semibold tw-leading-tight',
    'title-lg': 'tw-text-xl tw-font-semibold tw-leading-tight',
    display: 'tw-text-3xl tw-font-semibold tw-leading-tight tw-tracking-tight'
};

const WM_TYPOGRAPHY_TONE_CLASSNAMES = {
    foreground: 'tw-text-foreground',
    muted: 'tw-text-muted-foreground',
    background: 'tw-text-background',
    info: 'tw-text-info',
    success: 'tw-text-success',
    warning: 'tw-text-warning',
    destructive: 'tw-text-destructive',
    inherit: ''
};

const resolveTextVariantClassName = variant => (
    WM_TEXT_VARIANT_CLASSNAMES[variant] ?? WM_TEXT_VARIANT_CLASSNAMES.body
);

const resolveHeadingVariantClassName = variant => (
    WM_HEADING_VARIANT_CLASSNAMES[variant] ?? WM_HEADING_VARIANT_CLASSNAMES.title
);

const resolveTypographyToneClassName = tone => (
    WM_TYPOGRAPHY_TONE_CLASSNAMES[tone] ?? WM_TYPOGRAPHY_TONE_CLASSNAMES.foreground
);

const Text = React.forwardRef(({ as: Component = 'span', className: _className, style: _style, tone = 'foreground', variant = 'body', ...props }, ref) => {
    assertAllowedElement(Component, TEXT_ALLOWED_ELEMENTS, 'Text');

    return (
        <Component
            ref={ref}
            className={cn('tw-m-0', resolveTextVariantClassName(variant), resolveTypographyToneClassName(tone))}
            {...props}
        />
    );
});

Text.displayName = 'Text';

const Heading = React.forwardRef(({ as, className: _className, style: _style, tone = 'foreground', variant = 'title', ...props }, ref) => {
    const Component = as ?? HEADING_VARIANT_DEFAULT_ELEMENTS[variant] ?? 'h2';

    assertAllowedElement(Component, HEADING_ALLOWED_ELEMENTS, 'Heading');

    return (
        <Component
            ref={ref}
            className={cn('tw-m-0', resolveHeadingVariantClassName(variant), resolveTypographyToneClassName(tone))}
            {...props}
        />
    );
});

Heading.displayName = 'Heading';

export {
    Heading,
    Text
};
